// Module Import
//---------------------------------
import React from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import TypographyAtom from '../typography/typography'
import { brandGrey, brandOrange, brandSoftGrey } from '../../../assets/consts/color'
import checkIcon from '../../../assets/images/icon_check.svg'
import { primaryFont, secondaryFont } from '../../../assets/consts/fonts'

// Component Import
//---------------------------------

// Styled Component Declarations
//---------------------------------
const FieldWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};

  &[aria-required='true']::after {
    position: absolute;
    top: 0;
    left: 16px;
    content: '*';
    color: red;
  }
`

const CheckBoxTrigger = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border: 1px solid ${brandGrey};
  cursor: pointer;
  transition:
    background-color 0.25s ease-in-out,
    color 0.25s ease-in-out;

  &:hover {
    border-color: ${brandOrange};
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    display: block;
    width: 100%;
    height: 100%;
    background-color: ${brandOrange};
    background-image: url(${checkIcon});
    background-repeat: no-repeat;
    background-size: 70%;
    background-position: center;
    opacity: 0;
    transform: translate(-50%, -50%);
    transition:
      opacity 0.25s ease-in-out,
      color 0.25s ease-in-out;
  }
`

const FieldLabel = styled.label`
  color: ${brandSoftGrey};
  font-family: 'F37 Bolton';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px; /* 87.5% */
  display: ${({ show, $float }) => {
    if (!show) return 'none'
    if ($float) return 'block'
    return 'flex'
  }};
  gap: ${({ show, $float }) => (show && !$float ? '24px' : 0)};
  align-items: ${({ show, $float }) => (show && !$float ? 'center' : 'stretch')};
  width: 100%;

  p {
    font-family: ${primaryFont};
  }

  ${({ $float }) =>
    $float &&
    css`
      line-height: 1.35;

      /* &::after {
        content: '';
        display: table;
        clear: both;
      } */

      & > ${CheckBoxTrigger} {
        float: left;
        margin-right: 12px;
        margin-top: 3px;
        width: 16px;
        height: 16px;
      }

      .field-checkbox-float-copy {
        display: inline;
        line-height: 1.35;
        font-family: ${primaryFont};
      }
    `}
`

export const InputField = styled.input`
  display: flex;
  padding: 16px;
  background-color: transparent;
  align-self: stretch;
  border: 0;
  border-bottom: 2px solid ${brandSoftGrey};
  transition: all 0.25s;
  color: ${brandSoftGrey};

  font-family: ${secondaryFont};
  font-size: 16px;
  font-style: normal;
  font-weight: 300;
  line-height: 100%; /* 20px */
  width: 100%;

  input[type='number']::-webkit-inner-spin-button {
    opacity: 1;
  }

  &[type='checkbox'] {
    display: none;
  }

  &:focus {
    outline: none;
    border-bottom: 2px solid ${brandOrange};
  }

  &:hover {
    border-bottom: 2px solid ${brandOrange};
  }

  &:checked ~ label > ${CheckBoxTrigger} {
    border-color: ${brandOrange};

    &::after {
      opacity: 1;
    }
  }
`

// Component Export
//---------------------------------
export default function Field({
  labelText = '',
  labelContent,
  labelFor,
  placeholderText,
  fieldType,
  fieldId,
  fieldName,
  required,
  fullWidth,
  float,
  ...prop
}) {
  const checkboxFloat = Boolean(float && fieldType === 'checkbox')

  const checkboxCopyClassName = checkboxFloat ? 'field-checkbox-float-copy' : undefined

  const renderCheckboxLabel = () => {
    if (labelContent) {
      return <span className={checkboxCopyClassName}>{labelContent}</span>
    }

    return (
      <TypographyAtom
        as="span"
        type="P"
        copy={labelText}
        weight="Regular"
        className={checkboxCopyClassName}
      />
    )
  }

  return (
    <FieldWrapper aria-required={required} fullWidth={fullWidth}>
      <InputField
        type={fieldType}
        id={fieldId}
        name={fieldName}
        placeholder={placeholderText}
        {...prop}
      />
      <FieldLabel htmlFor={labelFor} show={fieldType === 'checkbox'} $float={checkboxFloat}>
        {fieldType === 'checkbox' && <CheckBoxTrigger />}
        {fieldType === 'checkbox' ? (
          renderCheckboxLabel()
        ) : (
          <TypographyAtom as="span" type="P" copy={labelText} weight="Regular" />
        )}
      </FieldLabel>
    </FieldWrapper>
  )
}

Field.propTypes = {
  labelText: PropTypes.string,
  /** Альтернатива `labelText` для `fieldType="checkbox"` (наприклад, з посиланнями). */
  labelContent: PropTypes.node,
  labelFor: PropTypes.string.isRequired,
  placeholderText: PropTypes.string.isRequired,
  fieldType: PropTypes.string.isRequired,
  fieldId: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  /** Для `fieldType="checkbox"`: чекбокс зліва, текст обтікає блок (CSS float). */
  float: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
}
